import { Link } from "react-router-dom";
import { Phone, ArrowRight, ArrowLeft, Building, Users, Rocket, FileCheck, Globe, Gem, TrendingUp, Landmark, BookOpen, FileBarChart, HandCoins, Calculator } from "lucide-react";
import { COMPANY } from "../constants/company";
import { Button } from "../components/ui/button";

const corporate = [
  { icon: Users, form: "Form 1065", title: "Partnerships", text: "We reflect with millimeter precision the distribution of profits and losses among partners (K-1), avoiding internal legal conflicts and audits." },
  { icon: Building, form: "Form 1120 / 1120-S", title: "C & S Corporations", text: "We optimize the salary vs. dividend structure to legally reduce Self-Employment Tax and protect the corporate entity." },
  { icon: Rocket, form: "Initial Strategy", title: "Start-Ups & Formation", text: "50% of tax success is decided at formation. We advise from day one to choose the structure that provides the greatest tax benefits." },
];

const individual = [
  { icon: FileCheck, form: "Total Regulatory Compliance", title: "Federal and State Returns", text: "Tax laws change annually. Generic software does not understand your life context. We analyze every possible deduction to ensure you don't pay a penny more than required, managing the complexity of changing state laws." },
  { icon: Globe, form: "Out-of-State Management", title: "Multi-State Taxes", text: "Did you move recently or work remotely? Rules change when crossing state lines. We manage returns in multiple jurisdictions to ensure compliance in all 50 states without double taxation." },
  { icon: Gem, form: "Wealth Protection", title: "Gift & Estate Tax", text: "Asset transfer triggers complex taxes (Form 709/706). We plan strategically so your beneficiaries receive what you built, minimizing tax intervention in your family legacy." },
  { icon: TrendingUp, form: "Long-Term Optimization", title: "Strategic Planning", text: "We don't just look at the past. We help you defer income and reduce taxes on your investments and retirement distributions, ensuring the sustainability of your future lifestyle." },
];

const bookkeeping = [
  { icon: Landmark, title: "Bank Reconciliation", text: "Immediate detection of bank errors or duplicate charges." },
  { icon: FileBarChart, title: "Financial Statements (P&L)", text: "Accurate documentation required for loans and investment rounds." },
  { icon: HandCoins, title: "Aging Reports", text: "Total cash flow control: know who owes you and when to collect." },
  { icon: Calculator, title: "Payroll Management", text: "Correct classification of employees/contractors to avoid fines." },
];

function ServiceCard({ item, testid }) {
  return (
    <div data-testid={testid} className="bg-card border border-border rounded-md p-6 hover:-translate-y-1 hover:shadow-md transition-transform duration-200">
      <item.icon className="w-7 h-7 text-primary" />
      <h3 className="mt-4 font-heading text-xl font-semibold">{item.title}</h3>
      {item.form && <p className="text-xs tracking-[0.15em] uppercase text-secondary-foreground/70 font-medium mt-1 text-muted-foreground">{item.form}</p>}
      <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{item.text}</p>
    </div>
  );
}

export default function Services() {
  return (
    <div className="min-h-screen bg-background" data-testid="services-page">
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="services-logo-link" className="flex items-center">
            <img src="/logo.png" alt="Laronta Pro Tax Services" className="h-11 w-auto rounded-sm" />
          </Link>
          <div className="flex items-center gap-3">
            <Button asChild variant="ghost" size="sm">
              <Link to="/" data-testid="services-back-link"><ArrowLeft className="w-4 h-4 mr-1" /> Home</Link>
            </Button>
            <Button asChild size="sm">
              <Link to="/auth" data-testid="services-login-button">Client Login</Link>
            </Button>
          </div>
        </div>
      </header>

      <section className="relative" data-testid="services-hero">
        <div className="absolute inset-0">
          <img src="/ceo-desk.png" alt="Laronta Pro Tax Services office meeting" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/50" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-24 lg:py-32">
          <p className="text-xs tracking-[0.25em] uppercase text-secondary font-medium animate-fade-up">Our Services</p>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-heading font-semibold tracking-tighter text-white max-w-2xl leading-[1.05] animate-fade-up" style={{ animationDelay: "80ms" }}>
            More than taxes. We build strategy.
          </h1>
          <p className="mt-5 text-lg text-white/85 max-w-xl animate-fade-up" style={{ animationDelay: "160ms" }}>
            Corporate tax strategy, individual returns, and bookkeeping — whether an LLC, S-Corp, or C-Corp, we ensure your legal structure works in your favor, maximizing profits and minimizing risks.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-24" data-testid="corporate-section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <p className="text-xs tracking-[0.25em] uppercase text-muted-foreground font-medium">Corporate Tax Strategy</p>
            <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-heading font-semibold tracking-tight">
              Starting a business? 50% of tax success is defined at formation.
            </h2>
            <p className="mt-4 text-muted-foreground max-w-xl">
              We advise you to choose the right entity from day one — LLC, S-Corp, or C-Corp — so your structure legally minimizes what you owe and protects what you build.
            </p>
          </div>
          <div className="md:col-span-5 rounded-md overflow-hidden border border-border">
            <img src="/team-office.png" alt="The Laronta Pro Tax Services team in the office" className="w-full h-full object-cover min-h-[240px]" />
          </div>
        </div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          {corporate.map((s, i) => <ServiceCard key={s.title} item={s} testid={`corporate-card-${i}`} />)}
        </div>
      </section>

      <section className="bg-muted/50 border-y border-border" data-testid="individual-section">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-5 rounded-md overflow-hidden border border-border order-2 md:order-1">
              <img src="/team-group.png" alt="Tax solutions presentation at Laronta Pro Tax Services" className="w-full h-full object-cover min-h-[240px]" />
            </div>
            <div className="md:col-span-7 order-1 md:order-2">
              <p className="text-xs tracking-[0.25em] uppercase text-muted-foreground font-medium">Individual Taxes</p>
              <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-heading font-semibold tracking-tight">
                Preparing your own taxes can be stressful and risky.
              </h2>
              <p className="mt-4 text-muted-foreground max-w-xl">
                We transform that complexity into a strategy for savings and compliance — from federal and state returns to multi-state moves, estates, and long-term planning.
              </p>
            </div>
          </div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {individual.map((s, i) => <ServiceCard key={s.title} item={s} testid={`individual-card-${i}`} />)}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-24" data-testid="bookkeeping-section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <p className="text-xs tracking-[0.25em] uppercase text-muted-foreground font-medium">Bookkeeping & Payroll</p>
            <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-heading font-semibold tracking-tight">
              Success starts with organized books.
            </h2>
            <p className="mt-4 text-muted-foreground max-w-xl">
              Many business owners underestimate bookkeeping until an audit arrives or they need a loan. We don't just "enter data" — we analyze your business health month by month.
            </p>
          </div>
          <div className="md:col-span-5 rounded-md overflow-hidden border border-border">
            <img src="/bookkeeping.png" alt="Bookkeeping and payroll strategy meeting at Laronta Pro Tax Services" className="w-full h-full object-cover min-h-[240px]" />
          </div>
        </div>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {bookkeeping.map((s, i) => <ServiceCard key={s.title} item={s} testid={`bookkeeping-card-${i}`} />)}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24" data-testid="services-cta">
        <div className="bg-primary rounded-md px-10 py-14 md:px-16 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div>
            <h2 className="text-3xl font-heading font-semibold tracking-tight text-primary-foreground">Not sure which service you need?</h2>
            <p className="mt-2 text-primary-foreground/75 text-sm max-w-md">Book a consultation and we'll map your situation to the right strategy.</p>
          </div>
          <div className="flex flex-wrap gap-4">
            <Button asChild size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/90">
              <Link to="/auth" data-testid="services-cta-book-button">Book a Consultation <ArrowRight className="w-4 h-4 ml-2" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white/40 text-white bg-white/10 hover:bg-white/20 hover:text-white">
              <a href={COMPANY.phoneHref} data-testid="services-cta-call-button"><Phone className="w-4 h-4 mr-2" /> {COMPANY.phone}</a>
            </Button>
          </div>
        </div>
      </section>

      <footer className="border-t border-border" data-testid="services-footer">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row justify-between gap-6 text-sm text-muted-foreground">
          <img src="/logo.png" alt="Laronta Pro Tax Services" className="h-9 w-auto rounded-sm self-start" />
          <p>{COMPANY.fullAddress} · {COMPANY.phone}</p>
          <div className="flex gap-6">
            <Link to="/team" data-testid="services-footer-team-link" className="hover:text-foreground transition-colors">Team</Link>
            <Link to="/support" data-testid="services-footer-support-link" className="hover:text-foreground transition-colors">Support</Link>
            <Link to="/privacy" data-testid="services-footer-privacy-link" className="hover:text-foreground transition-colors">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
